import { FormInstance, FormItemProps } from 'antd';
import { requiredRule } from 'const/formRules';
import dayjs, { Dayjs } from 'dayjs';
import { t as trsl } from 'i18next';
import {
  NouveauDossierConcessionnaireData,
  YesOrNo,
} from 'types/Concessionnaire';

export const TRA_EQ_117_V83_START_DATE = '2026-06-01';

export const M1_CONDITIONAL_QUESTION_NAMES = [
  'scoreEnvironnementalAtteint',
  'coutAcquisitionInferieur47000',
  'masseVehiculeInferieure2400',
  'dejaBeneficieBonusEcologique',
  'produitEnEurope',
  'vehiculeAdeme',
] as const;

export const dateDevisRules: Record<'3.1' | '3.2', FormItemProps['rules']> = {
  '3.1': [
    requiredRule,
    {
      validator: async (_: unknown, value: Dayjs) => {
        // Verifie si la date est anterieure de plus de 30 jours
        if (value && value.isBefore(dayjs().subtract(30, 'days'), 'day')) {
          throw new Error(trsl('nouveau_formulaire:date_devis:error'));
        }

        return Promise.resolve();
      },
    },
  ],
  '3.2': [
    requiredRule,
    {
      validator: async (_: unknown, value: Dayjs) => {
        if (value && !value.isBefore(dayjs().add(1, 'days'), 'day')) {
          throw new Error(trsl('nouveau_formulaire:date_edition_devis:error'));
        }

        return Promise.resolve();
      },
    },
  ],
};

export function getDatePrevisionnelleDevisRules(
  personnePhysique = false,
  form?: FormInstance<NouveauDossierConcessionnaireData>,
) {
  const rules: FormItemProps['rules'] = [requiredRule];

  if (!personnePhysique) {
    rules.push({
      validator: async (_: unknown, value: Dayjs) => {
        // Verifie si la date est anterieure a aujourd'hui
        if (value && value.isBefore(dayjs(), 'day')) {
          throw new Error(
            trsl('nouveau_formulaire:date_previsionnelle_devis:error'),
          );
        }

        return Promise.resolve();
      },
    });
  }

  if (personnePhysique && form) {
    rules.push({
      validator: async (_: unknown, value: Dayjs) => {
        const dateEditionDevis = form.getFieldValue('dateEditionDevis');

        if (value && value.isBefore(dayjs(TRA_EQ_117_V83_START_DATE), 'day')) {
          throw new Error(
            trsl('nouveau_formulaire:date_previsionnelle_devis:error_v83_min'),
          );
        }

        // Verifie si la date previsionnelle est strictement inferieure a la date d'edition
        if (
          value &&
          dateEditionDevis &&
          value.isBefore(dateEditionDevis, 'day')
        ) {
          throw new Error(
            trsl(
              'nouveau_formulaire:date_previsionnelle_devis:error_before_edition',
            ),
          );
        }

        return Promise.resolve();
      },
    });
  }

  return rules;
}

export const nbVehiculesRules: FormItemProps['rules'] = [
  requiredRule,
  {
    // Gestion du cas ou l'utilisateur entre un nombre negatif ou egal a 0
    validator: async (_: unknown, value: number) => {
      if (value <= 0) {
        throw new Error(trsl('nouveau_formulaire:nb_vehicules:error'));
      }

      return Promise.resolve();
    },
  },
];

export const usageVehiculesLegerRules: FormItemProps['rules'] = [
  requiredRule,
  {
    validator: async (_: unknown, value: string) => {
      if (value === 'location_courte') {
        throw new Error(
          trsl('nouveau_formulaire:usage_vehicules:error_courte_duree'),
        );
      }

      return Promise.resolve();
    },
  },
];

export const m1ConditionalQuestionRules: FormItemProps['rules'] = [
  { required: true },
];

export const yesNoQuestionValues = [YesOrNo.OUI, YesOrNo.NON] as const;

export const reverseFieldOrderClassName =
  '[&_.ant-form-item-control]:flex [&_.ant-form-item-control]:flex-col-reverse [&_.ant-form-item-extra]:order-3 [&_.ant-form-item-extra]:mb-2 [&_.ant-form-item-control-input]:order-2';
